import { PropsWithChildren, useState, createContext, useContext, useMemo } from "react"
import { gql } from "~/gql"
import { useQuery } from "@oysterhr/core"
import { buildMoney } from "~/shared/components/VariableCompensationSummary/helpers"
import { convertUnitIntoSubunit } from "@oysterhr/common"
import { Employment, Money } from "@oysterhr/schema"
import {
  EmploymentRestrictiveCovenants,
  RestrictiveCovenantsFormValues,
  RestrictiveCovenantsProps,
  AreaOfBusinessKnowledgeBase,
} from "../types"
import { RestrictiveCovenantsRulesFragment } from "~/gql/graphql"
import {
  KBParams,
  useRestrictiveCovenantsIntelligence,
} from "./useRestrictiveCovenantsIntelligence"
import { initializeValuesWithKnowledgeBase } from "../helpers/initializeValuesWithKnowledgeBase"
import { LoadingSpinner } from "@oysterhr/ui"

type ContextType = {
  salary: Money
  employment: EmploymentRestrictiveCovenants
  defaultValues: RestrictiveCovenantsFormValues
  knowledgeBase: RestrictiveCovenantsRulesFragment | undefined
  areaOfBusinessKnowledgeBase: AreaOfBusinessKnowledgeBase | undefined
  loadingKnowledgeBase: boolean
  syncedKBParameters: KBParams
  setSyncedKBParameters: (params: KBParams) => void
} & Pick<RestrictiveCovenantsProps, "booleanFieldType" | "disabledFields" | "hiddenFields">

const RestrictiveConvenantsSectionContext = createContext<ContextType | null>(null)

const RESTRICTIVE_COVENANTS_SECTION_QUERY = gql(`
  query RestrictiveCovenantsSection($employmentId: ID!) {
    employment(employmentId: $employmentId) {
      id
      countryCode
      subdivisionCode
      salaryAmount
      salaryCurrencyCode
      restrictiveCovenants {
        ...RestrictiveCovenantsSectionFragment
      }
    }
  }
`)

export const useRestrictiveCovenantsContext = () => {
  const context = useContext(RestrictiveConvenantsSectionContext)
  if (!context) {
    throw new Error(
      "useRestrictiveCovenantsContext must be used within a RestrictiveConvenantsSectionContext",
    )
  }
  return context
}

type RestrictiveCovenantsContextProviderProps = PropsWithChildren<
  Pick<
    RestrictiveCovenantsProps,
    | "employmentId"
    | "countryCode"
    | "subdivisionCode"
    | "booleanFieldType"
    | "initialValuesFunction"
    | "disabledFields"
    | "hiddenFields"
  >
>

export const RestrictiveCovenantsContextProvider = ({
  children,
  employmentId,
  countryCode,
  subdivisionCode,
  booleanFieldType = "toggle",
  initialValuesFunction,
  disabledFields,
  hiddenFields,
}: RestrictiveCovenantsContextProviderProps) => {
  const [syncedKBParameters, setSyncedKBParameters] = useState<KBParams>({})

  const { data, loading: loadingRestrictiveCovenantSection } = useQuery(
    RESTRICTIVE_COVENANTS_SECTION_QUERY,
    { variables: { employmentId } },
  )
  // @todo: fix users of this data to only request the data needed
  const employment = data?.employment as Employment

  const {
    data: restrictiveCovenantIntelligence,
    previousData: restrictiveCovenantIntelligencePrevious,
    loading: loadingKnowledgeBase,
  } = useRestrictiveCovenantsIntelligence({
    countryCode,
    subdivisionCode,
    ...syncedKBParameters,
  })

  const { salaryAmount, salaryCurrencyCode } = (employment || {}) as Employment
  const salary = useMemo(() => {
    return buildMoney({
      currencyCode: salaryCurrencyCode || undefined,
      subunit: convertUnitIntoSubunit(salaryAmount || 0, salaryCurrencyCode || "USD"),
    })
  }, [salaryAmount, salaryCurrencyCode])

  const knowledgeBaseExists =
    restrictiveCovenantIntelligence?.countryIntelligence?.restrictiveCovenants ||
    (loadingKnowledgeBase &&
      restrictiveCovenantIntelligencePrevious?.countryIntelligence?.restrictiveCovenants)

  const knowledgeBase = knowledgeBaseExists
    ? restrictiveCovenantIntelligence?.countryIntelligence?.restrictiveCovenants ||
      restrictiveCovenantIntelligencePrevious?.countryIntelligence?.restrictiveCovenants
    : undefined

  const areaOfBusinessKnowledgeBase = restrictiveCovenantIntelligence?.countryIntelligence
    ? {
        companyAreaOfBusinessFieldRequired:
          restrictiveCovenantIntelligence.countryIntelligence.companyAreaOfBusinessFieldRequired,
        companyAreaOfBusinessFieldForSubdivisionCodes:
          restrictiveCovenantIntelligence.countryIntelligence
            .companyAreaOfBusinessFieldForSubdivisionCodes,
      }
    : undefined

  const defaultValues = useMemo(() => {
    return initializeValuesWithKnowledgeBase({
      knowledgeBase,
      employment,
      initialValuesFunction,
    })
  }, [knowledgeBase, employment, initialValuesFunction])

  return (
    <RestrictiveConvenantsSectionContext.Provider
      value={{
        salary,
        booleanFieldType,
        employment,
        defaultValues,
        knowledgeBase,
        areaOfBusinessKnowledgeBase,
        loadingKnowledgeBase,
        syncedKBParameters,
        setSyncedKBParameters,
        disabledFields,
        hiddenFields,
      }}
    >
      {!loadingRestrictiveCovenantSection ? (
        children
      ) : (
        <div className="relative mt-8 pt-8">
          <LoadingSpinner />
        </div>
      )}
    </RestrictiveConvenantsSectionContext.Provider>
  )
}
